import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface Actor {
  id: string;
  name: string;
  image: string;
  ethnicity?: string;
}

// Map CSV ethnicity values to our filter values
const mapEthnicity = (csvEthnicity: string): string => {
  const mapping: Record<string, string> = {
    "White": "caucasian",
    "Asian": "asian",
    "Ebony": "black",
    "Indian": "indian",
    "Hijabi": "middle-eastern",
    "Latina": "latina",
    "Anime": "anime",
  };
  return mapping[csvEthnicity] || csvEthnicity.toLowerCase();
};

// Anime characters from public/baddies/
const animeCharacters: Actor[] = [
  {
    id: "anime1",
    name: "Megumin",
    image: "/baddies/anime1.jpg",
    ethnicity: "anime",
  },
  {
    id: "anime2",
    name: "Nezuko",
    image: "/baddies/anime2.jpg",
    ethnicity: "anime",
  },
  {
    id: "anime3",
    name: "Mitsuri",
    image: "/baddies/anime3.jpg",
    ethnicity: "anime",
  },
  {
    id: "anime4",
    name: "Power",
    image: "/baddies/anime4.jpg",
    ethnicity: "anime",
  },
];

// Empty fallback - will be loaded from CSV
const baddieCharactersFallback: Actor[] = [];

const NoFilter: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"topPicks" | "viewAll">(
    "topPicks"
  );
  const [selectedEthnicity, setSelectedEthnicity] = useState<string>("all");
  const [baddieCharacters, setBaddieCharacters] = useState<Actor[]>(baddieCharactersFallback);
  const [topPicksCharacters, setTopPicksCharacters] = useState<Actor[]>([]);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [visibleImages, setVisibleImages] = useState<Set<string>>(new Set());

  // Load characters from CSV on mount
  useEffect(() => {
    const loadCharacters = async () => {
      try {
        const response = await fetch('/new_assets.csv');
        const csvText = await response.text();
        
        // Parse CSV properly (handles commas in quoted fields)
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };
        
        const lines = csvText.split('\n');
        const headers = parseCSVLine(lines[0]);
        const nameIndex = headers.indexOf('Name');
        const urlIndex = headers.indexOf('URL');
        const ethnicityIndex = headers.indexOf('Ethnicity (IMPORTANT)');
        
        const actors: Actor[] = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          const values = parseCSVLine(line);
          if (values.length >= 7) { // We need at least 7 columns
            const name = values[nameIndex]?.trim() || `Actor ${i}`;
            const image = values[urlIndex]?.trim() || '';
            const ethnicity = values[ethnicityIndex]?.trim() || '';
            
            if (image && name) {
              actors.push({
                id: `actor-${i}`,
                name: name,
                image: image,
                ethnicity: mapEthnicity(ethnicity),
              });
            }
          }
        }
        
        console.log(`Loaded ${actors.length} actors from CSV`);
        
        // Add anime characters at the end
        const allActors = [...actors, ...animeCharacters];
        setBaddieCharacters(allActors);
        
        // Shuffle and select 10 random actors for Top Picks (from all including anime)
        const shuffled = [...allActors].sort(() => Math.random() - 0.5);
        setTopPicksCharacters(shuffled.slice(0, 10));
      } catch (error) {
        console.error("Error loading CSV:", error);
      }
    };

    loadCharacters();
  }, []);

  // Track page view on mount (disabled for showcase)
  useEffect(() => {
    // Analytics removed for showcase site
  }, []);

  const handleImageLoad = (imageUrl: string) => {
    setLoadedImages((prev) => new Set(prev).add(imageUrl));
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const imgSrc = entry.target.getAttribute("data-src");
            if (imgSrc) {
              setVisibleImages((prev) => new Set(prev).add(imgSrc));
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        rootMargin: "200px", // Start loading 200px before entering viewport
        threshold: 0.01,
      }
    );

    // Observe all lazy-load elements
    const lazyElements = document.querySelectorAll("[data-lazy-load]");
    lazyElements.forEach((el) => observer.observe(el));

    return () => {
      lazyElements.forEach((el) => observer.unobserve(el));
    };
  }, [activeTab, selectedEthnicity]);

  // Preload top picks images immediately
  useEffect(() => {
    if (topPicksCharacters.length > 0) {
      topPicksCharacters.forEach((character) => {
        setVisibleImages((prev) => new Set(prev).add(character.image));
      });
    }
  }, [topPicksCharacters]);

  const handleCharacterClick = (character: Actor) => {
    // Navigate to image-to-video with wan2.2-nofilter model and character image pre-loaded
    navigate("/video/i2v", {
      state: {
        referenceImage: character.image,
        selectedModel: "wan2.2-nofilter",
        noFilterMode: true,
      },
    });
  };

  const handleImageToVideoNoFilter = () => {
    // Navigate to image-to-video with wan2.2-nofilter model preselected
    navigate("/video/i2v", {
      state: {
        selectedModel: "wan2.2-nofilter",
        noFilterMode: true,
      },
    });
  };

  return (
    <div className="min-h-screen bg-black dark:bg-black p-6 pb-32 md:pb-6 transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* View Examples Button */}
        <div className="flex justify-center pt-4">
          <button
            onClick={() => navigate("/no-filter/examples")}
            className="group relative bg-white text-black text-lg font-bold px-10 py-4 rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_rgba(255,255,255,0.5)] hover:scale-105 animate-pulse-subtle"
          >
            <span className="flex items-center gap-3">
              <svg
                className="w-6 h-6"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fillRule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clipRule="evenodd"
                />
              </svg>
              View Examples
              <svg
                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </span>
          </button>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Quick Create
          </h2>
          <div className="flex justify-center">
            {/* Image to Video No Filter */}
            <div
              onClick={handleImageToVideoNoFilter}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-2 border-white/60 shadow-[0_0_30px_rgba(255,255,255,0.15)] hover:border-white hover:shadow-[0_0_50px_rgba(255,255,255,0.25)] transition-all duration-300 cursor-pointer hover:-translate-y-2 hover:scale-105 p-8 w-full max-w-2xl animate-slide-up animation-delay-200"
            >
              {/* Subtle animated glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative flex items-center justify-between gap-8">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                    Image to Video
                    <svg
                      className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </h3>
                  <p className="text-sm md:text-lg text-gray-300">
                    Upload your own image
                  </p>
                </div>
                <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:bg-white/20 transition-all duration-300 flex-shrink-0 shadow-lg group-hover:shadow-white/30">
                  <svg
                    className="w-10 h-10 text-white dark:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {/* Dotted box border */}
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="18"
                      rx="2"
                      strokeWidth="1.5"
                      strokeDasharray="2 2"
                      fill="none"
                    />
                    {/* Simple image/photo icon */}
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      transform="scale(0.6) translate(8, 8)"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              Or select a character below to use their image
            </p>
          </div>
        </div>

        {/* Popular Characters */}
        <div>
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Pick a Model
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Ask your model to do anything you can think of.
            </p>
          </div>

          {/* Tab Bar and Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            {/* Tab Bar */}
            <div className="inline-flex rounded-lg bg-white/5 p-1 transition-colors duration-300 border border-white/10">
              <button
                onClick={() => {
                  setActiveTab("topPicks");
                  // Reset filter to "All" when switching to Top Picks
                  setSelectedEthnicity("all");
                }}
                className={`px-6 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                  activeTab === "topPicks"
                    ? "bg-white text-black shadow-md"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                Top Picks (10)
              </button>
              <button
                onClick={() => setActiveTab("viewAll")}
                className={`px-6 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                  activeTab === "viewAll"
                    ? "bg-white text-black shadow-md"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                View All (300+)
              </button>
            </div>

            {/* Filter Section */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-300">
                Ethnicity:
              </span>
              <select
                value={selectedEthnicity}
                onChange={(e) => {
                  setSelectedEthnicity(e.target.value);
                  // Automatically switch to View All when filter changes
                  if (e.target.value !== "all") {
                    setActiveTab("viewAll");
                  }
                }}
                className="px-4 py-2 text-sm font-medium bg-white/5 border border-white/20 rounded-lg text-white hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All</option>
                <option value="caucasian">White</option>
                <option value="asian">Asian</option>
                <option value="black">Ebony</option>
                <option value="indian">Indian</option>
                <option value="middle-eastern">Hijabi</option>
                <option value="latina">Latina</option>
                <option value="anime">Anime</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {(activeTab === "topPicks"
              ? topPicksCharacters
              : baddieCharacters
            )
              .filter((character) =>
                selectedEthnicity === "all"
                  ? true
                  : character.ethnicity === selectedEthnicity
              )
              .map((character, index) => {
              const animationDelay = `animation-delay-${Math.min(
                (index + 3) * 100,
                900
              )}`;
              const isTopPick = activeTab === "topPicks";
              const shouldLoad = isTopPick || visibleImages.has(character.image);
              const isLoaded = loadedImages.has(character.image);
              
              return (
                <div
                  key={character.id}
                  onClick={() => handleCharacterClick(character)}
                  className={`group relative cursor-pointer rounded-xl bg-gray-900 dark:bg-gray-900 border-2 border-white/20 dark:border-white/20 hover:border-white/40 dark:hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 hover:-translate-y-2 hover:scale-105 animate-slide-up ${animationDelay} overflow-hidden aspect-square`}
                  data-lazy-load
                  data-src={character.image}
                >
                  {shouldLoad && !isLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    </div>
                  )}
                  {shouldLoad ? (
                    <img
                      src={character.image}
                      alt={character.name}
                      className={`w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300 ${
                        isLoaded ? "opacity-100" : "opacity-0"
                      }`}
                      onLoad={() => handleImageLoad(character.image)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-800"></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 transform translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                    <h3 className="text-white font-semibold text-sm text-center drop-shadow-lg">
                      {character.name}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Example Videos */}
        <div>
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-white mb-2">
              Example Creations
            </h2>
            <p className="text-gray-300">
              See what's possible with No Filter
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-6">
            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer hover:scale-105">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/final_8s.mp4"
                poster="/baddies/baddie14.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Mila's Secret
                </h3>
              </div>
            </div>

            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer hover:scale-105">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example_2.mp4"
                poster="/baddies/baddie1.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Aria's Dressover
                </h3>
              </div>
            </div>

            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer hover:scale-105">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example_3.mp4"
                poster="/baddies/baddie10.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Evelyn's Happy Place
                </h3>
              </div>
            </div>

            <div className="group relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer hover:scale-105">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example_4.mp4"
                poster="/baddies/baddie12.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="text-white font-semibold text-sm drop-shadow-lg">
                  Emily Wilding Out
                </h3>
              </div>
            </div>
          </div>

          {/* View More Button */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate("/no-filter/examples")}
              className="group relative bg-white text-black text-lg font-bold px-10 py-4 rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-3"
            >
              View More Examples
              <svg
                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10 rounded-xl p-6 text-center transition-colors duration-300">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Important Disclaimer
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed max-w-3xl mx-auto">
            The "No Filter" mode is designed for creative and entertainment
            purposes. Users are responsible for ensuring their content complies
            with platform guidelines and applicable laws. PixelMaker does not
            endorse or take responsibility for user-generated content.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NoFilter;
